import {
    AfterContentInit,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
} from '@angular/core';

import { IWaveform } from '../../models/iwaveform';
import { ChannelType } from '../../models/ichannel';
import { LayerViewModel } from '../../view-models/layer-viewmodel';
import { TraceViewModel } from '../../view-models/trace-viewmodel';
import { Layout } from '../../layout/layout';
import { ArrangementType } from '../../models/enums';
import { TraceData } from '../../view-models/trace-data';
import { ReferenceTraceViewModel } from '../../view-models/reference-trace-viewmodel';
import { NgForOf } from '@angular/common';
import { TraceComponent } from '../trace/trace.component';

@Component({
    selector: '[chart-layer]',
    templateUrl: './layer.component.html',
    styleUrls: ['./layer.component.css'],
    imports: [NgForOf, TraceComponent],
    standalone: true,
})
export class LayerComponent implements AfterContentInit, OnChanges, OnDestroy {
    // events

    @Output()
    layoutChange = new EventEmitter<LayerViewModel>();

    // constructor
    @Input()
    layer: LayerViewModel;

    // properties
    @Input()
    tracesSynchronized = true;
    @Input()
    arrangement?: ArrangementType;
    visible = false;

    constructor(public layout: Layout) {
    }

    // public functions

    ngAfterContentInit() {
        this.layout.update();
        this.layoutChange.emit(this.layer);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.layer || (changes.arrangement && !changes.arrangement.firstChange)) {
            this.updateLayer();
        }
    }

    ngOnDestroy() {
        this.layer.traces.splice(0, this.layer.traces.length);
        this.layoutChange.emit(this.layer);
    }

    // private functions

    private updateLayer() {
        if (this.layer == null) {
            return;
        }

        this.layer.traces.splice(0, this.layer.traces.length);

        this.buildTraces(this.layer.ecgData.originalWaveform, this.layer.ecgData.derivedWaveform);

        this.layout.updateSingle(this.layer, true);
    }

    private buildTraces(originalWaveform: IWaveform, derivedWaveform: IWaveform) {
        if (originalWaveform.channels == null || derivedWaveform.channels == null) return;

        originalWaveform.channels.sort((channel) => channel.order);
        derivedWaveform.channels.sort((channel) => channel.order);

        this.layer.leadCount = originalWaveform.channels.length;

        originalWaveform.channels.forEach((originalChannel, index) => {
            let derivedChannel = derivedWaveform.channels[index];

            let traceData = new TraceData();
            traceData.originalChannel = originalChannel;
            traceData.derivedChannel = derivedChannel;

            const annotationsWaveform =
                this.arrangement == ArrangementType.Medians ? derivedWaveform : originalWaveform;

            annotationsWaveform.annotations.forEach((annotation) => {
                if (annotation.markers != null) {
                    annotation.markers.forEach((marker) => traceData.markers.push(marker));
                }
            });

            let trace = new TraceViewModel(this.layout, traceData);
            trace.appearance = this.layer.appearance;
            this.layer.traces.push(trace);
        });

        if (originalWaveform.channels.length === 12) {
            const channel = originalWaveform.channels.find((channel) => channel.type == ChannelType.LEAD_II);
            if (channel) {
                let referenceTrace = new ReferenceTraceViewModel(this.layout, channel);
                referenceTrace.appearance = this.layer.appearance;
                this.layer.referenceTraces.push(referenceTrace);
            }
        } else if (originalWaveform.channels.length === 15) {
            for (let i = 12; i < 15; i++) {
                let referenceTrace = new ReferenceTraceViewModel(this.layout, originalWaveform.channels[i]);
                referenceTrace.appearance = this.layer.appearance;
                this.layer.referenceTraces.push(referenceTrace);
            }
        }
    }
}
